<template>
  <div class="cash-card-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>现金分卡管理（支持老赖用户）</span>
          <el-button type="primary" @click="showGenerateDialog = true">生成新卡</el-button>
        </div>
      </template>
      
      <el-alert type="warning" :closable="false" style="margin-bottom: 20px">
        <template #title>
          <strong>现金分卡</strong>：支持银行卡/微信/支付宝被冻结的用户通过现金购买积分卡参与平台
        </template>
      </el-alert>

      <el-table :data="cards" border stripe>
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column prop="cardNumber" label="卡号" width="180" />
        <el-table-column prop="amount" label="面值" width="100">
          <template #default="{ row }">
            {{ row.amount }}元
          </template>
        </el-table-column>
        <el-table-column prop="creditAmount" label="实际价值" width="100">
          <template #default="{ row }">
            <el-tag type="success">{{ row.creditAmount }}分</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="statusMap[row.status]?.type">
              {{ statusMap[row.status]?.label }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180" />
        <el-table-column prop="usedAt" label="使用时间" width="180" />
      </el-table>

      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :total="pagination.total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next"
        @size-change="loadCards"
        @current-change="loadCards"
        style="margin-top: 20px; justify-content: flex-end"
      />
    </el-card>

    <!-- 生成新卡对话框 -->
    <el-dialog v-model="showGenerateDialog" title="生成现金分卡" width="500px">
      <el-form :model="generateForm" label-width="100px">
        <el-form-item label="面值">
          <el-select v-model="generateForm.amount" placeholder="请选择面值">
            <el-option label="100元" :value="100" />
            <el-option label="200元" :value="200" />
            <el-option label="500元" :value="500" />
            <el-option label="1000元" :value="1000" />
            <el-option label="2000元" :value="2000" />
            <el-option label="5000元" :value="5000" />
          </el-select>
        </el-form-item>
        <el-form-item label="购买方式">
          <el-select v-model="generateForm.purchaseMethod" placeholder="请选择">
            <el-option label="现金" value="cash" />
            <el-option label="银行转账" value="bank_transfer" />
            <el-option label="第三方支付" value="third_party" />
          </el-select>
        </el-form-item>
        <el-form-item label="手续费">
          <el-tag>5%</el-tag>
          <span style="margin-left: 10px">
            实际价值: {{ (generateForm.amount * 0.95).toFixed(2) }}分
          </span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showGenerateDialog = false">取消</el-button>
        <el-button type="primary" @click="generateCard">生成</el-button>
      </template>
    </el-dialog>

    <!-- 生成成功对话框 -->
    <el-dialog v-model="showResultDialog" title="现金分卡生成成功" width="500px">
      <el-descriptions :column="1" border>
        <el-descriptions-item label="卡号">{{ generatedCard.cardNumber }}</el-descriptions-item>
        <el-descriptions-item label="面值">{{ generatedCard.faceValue }}元</el-descriptions-item>
        <el-descriptions-item label="实际价值">{{ generatedCard.actualValue }}分</el-descriptions-item>
        <el-descriptions-item label="手续费">{{ generatedCard.fee }}元 ({{ generatedCard.feeRate }})</el-descriptions-item>
        <el-descriptions-item label="有效期">{{ generatedCard.expiredAt }}</el-descriptions-item>
        <el-descriptions-item label="二维码数据">{{ generatedCard.qrCodeData }}</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button type="primary" @click="showResultDialog = false">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { cashCardApi } from '@/api/modules/debt-neutralization';
import { ElMessage } from 'element-plus';

const cards = ref([]);
const showGenerateDialog = ref(false);
const showResultDialog = ref(false);
const generateForm = reactive({
  amount: 100,
  purchaseMethod: 'cash',
});
const generatedCard = ref({});
const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0,
});

const statusMap = {
  active: { label: '可用', type: 'success' },
  used: { label: '已使用', type: 'info' },
  expired: { label: '已过期', type: 'danger' },
};

const loadCards = async () => {
  try {
    const res = await cashCardApi.getList({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
    if (res.code === 200) {
      cards.value = res.data.list;
      pagination.total = res.data.pagination.total;
    }
  } catch (error) {
    ElMessage.error('加载失败');
  }
};

const generateCard = async () => {
  try {
    const res = await cashCardApi.generateCode(generateForm);
    if (res.code === 200) {
      generatedCard.value = res.data;
      showGenerateDialog.value = false;
      showResultDialog.value = true;
      loadCards();
      ElMessage.success('生成成功');
    }
  } catch (error) {
    ElMessage.error('生成失败');
  }
};

onMounted(() => {
  loadCards();
});
</script>

<style scoped>
.cash-card-management {
  padding: 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
